/**
 * ConnectivityService
 * Monitors network connectivity status using Capacitor Network plugin
 * Emits events on connectivity changes for sync triggering
 */

import { Network } from '@capacitor/network'
import type {
    ConnectivityStatus,
    ConnectivityChangeListener,
    NetworkStatusChangeEvent
} from './types'

class ConnectivityService {
    private listeners: Set<ConnectivityChangeListener> = new Set()
    private currentStatus: ConnectivityStatus = {
        connected: false,
        connectionType: 'unknown'
    }
    private isInitialized = false

    /**
     * Initialize the connectivity service
     * Sets up network status listener and checks initial status
     */
    async initialize(): Promise<void> {
        if (this.isInitialized) {
            console.warn('[ConnectivityService] Already initialized')
            return
        }

        try {
            // Get initial network status
            const status = await Network.getStatus()
            this.currentStatus = this.mapNetworkStatus(status)

            // Set up network status change listener
            Network.addListener('networkStatusChange', this.handleNetworkChange.bind(this))

            this.isInitialized = true
            console.log('[ConnectivityService] Initialized with status:', this.currentStatus)
        } catch (error) {
            console.error('[ConnectivityService] Failed to initialize:', error)
            // Fallback to navigator.onLine for web platform
            this.fallbackToNavigator()
        }
    }

    /**
     * Handle network status changes from Capacitor Network plugin
     */
    private handleNetworkChange(status: NetworkStatusChangeEvent): void {
        const previousStatus = this.currentStatus.connected
        this.currentStatus = this.mapNetworkStatus(status)

        console.log('[ConnectivityService] Network status changed:', this.currentStatus)

        // Notify all listeners
        this.notifyListeners()

        // Log transition
        if (previousStatus !== this.currentStatus.connected) {
            const transition = this.currentStatus.connected
                ? 'offline → online'
                : 'online → offline'
            console.log(`[ConnectivityService] Connectivity transition: ${transition}`)
        }
    }

    /**
     * Map Capacitor Network status to ConnectivityStatus
     */
    private mapNetworkStatus(status: NetworkStatusChangeEvent): ConnectivityStatus {
        return {
            connected: status.connected,
            connectionType: this.normalizeConnectionType(status.connectionType)
        }
    }

    /**
     * Normalize connection type to expected values
     */
    private normalizeConnectionType(
        type: string
    ): 'wifi' | 'cellular' | 'ethernet' | 'unknown' | 'none' {
        const lowerType = type.toLowerCase()
        if (lowerType === 'wifi') return 'wifi'
        if (lowerType === 'cellular') return 'cellular'
        if (lowerType === 'ethernet') return 'ethernet'
        if (lowerType === 'none') return 'none'
        return 'unknown'
    }

    /**
     * Fallback to navigator.onLine for web platform
     */
    private fallbackToNavigator(): void {
        this.currentStatus = {
            connected: navigator.onLine,
            connectionType: 'unknown'
        }

        window.addEventListener('online', () => {
            this.currentStatus = { connected: true, connectionType: 'unknown' }
            this.notifyListeners()
        })

        window.addEventListener('offline', () => {
            this.currentStatus = { connected: false, connectionType: 'none' }
            this.notifyListeners()
        })

        this.isInitialized = true
        console.log('[ConnectivityService] Using navigator.onLine fallback')
    }

    /**
     * Notify all registered listeners of status change
     */
    private notifyListeners(): void {
        this.listeners.forEach(listener => {
            try {
                listener(this.currentStatus)
            } catch (error) {
                console.error('[ConnectivityService] Error in listener:', error)
            }
        })
    }

    /**
     * Check if device is currently online
     */
    isOnline(): boolean {
        return this.currentStatus.connected
    }

    /**
     * Get current connectivity status
     */
    getStatus(): ConnectivityStatus {
        return { ...this.currentStatus }
    }

    /**
     * Subscribe to connectivity changes
     * Returns unsubscribe function
     */
    addListener(listener: ConnectivityChangeListener): () => void {
        this.listeners.add(listener)

        // Return unsubscribe function
        return () => {
            this.listeners.delete(listener)
        }
    }

    /**
     * Remove a specific listener
     */
    removeListener(listener: ConnectivityChangeListener): void {
        this.listeners.delete(listener)
    }

    /**
     * Remove all listeners
     */
    removeAllListeners(): void {
        this.listeners.clear()
    }

    /**
     * Cleanup service (for testing)
     */
    async destroy(): Promise<void> {
        this.removeAllListeners()
        this.isInitialized = false
        await Network.removeAllListeners()
    }
}

// Export singleton instance
export const connectivityService = new ConnectivityService()
export default connectivityService
